import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { ModelData, ConfigurationState, PricingData, PerkInfo } from '../types'
import { formatPrice } from '../utils/helpers'
import { isFusionAvailable, isFusionCornerAvailable, isAkuPlusAvailable } from '../utils/optionGenerator'
import { useImagePreloader } from '../hooks/useImagePreloader'
import { getNemoControllerFullLabel } from '../data/controllerData'


interface PreviewPanelProps {
  model: ModelData | null
  config: ConfigurationState
  pricing: PricingData
}


interface PerkData {
  icon: string
  value: string
  label: string
  color?: string
  active?: boolean
  glassType?: string
  handleType?: string
  ceramicType?: string
  variantType?: string
  bezelType?: string
  fusionType?: string
  akuPlusType?: string // AKU+ perk type
  accumulationMaterialType?: string // MATERIAŁY AKUMULACYJNE perk type
  thermalModulesType?: string // THERMAL MODULES perk type
  perkVariant?: string // Store variant info without generating CSS class
}


// Helper function to generate perk ID for Legacy JavaScript compatibility
const getPerkId = (perk: PerkData, index: number): string => {
  if (perk.glassType) return 'perk-glass'
  if (perk.handleType) return 'perk-handle'  
  if (perk.ceramicType) return 'perk-ceramic'
  if (perk.variantType) return 'perk-variant'
  if (perk.bezelType) return 'perk-bezel'
  if (perk.akuPlusType) return 'perk-aku-plus'
  if (perk.accumulationMaterialType) return `perk-${perk.accumulationMaterialType}`
  if (perk.thermalModulesType) return 'perk-thermal-modules'
  
  // For other perks, try to determine by icon or value
  if (perk.icon === '🚪' || perk.value?.includes('Standard') || perk.value?.includes('LIFT')) return 'perk-variant'
  if (perk.icon === '⬜' || perk.value?.includes('Bezel') || perk.value?.includes('bez')) return 'perk-bezel'
  if (perk.value?.includes('FUSION')) return 'perk-fusion'
  if (perk.value?.includes('AKU') || perk.icon === '🔋') return 'perk-aku'
  
  return `perk-${index}` // fallback
}

// Helper function to extract value from PerkInfo or return string as-is
const extractValue = (value: string | PerkInfo): string => {
  if (typeof value === 'string') {
    return value
  }
  return value.value
}

// Helper function to get perk image filename based on perk type and config
const getPerkImageFilename = (perkType: string, config: any): string | null => {
  switch (perkType) {
    case 'variant':
    case 'perk-variant':
      // Check both standard and vis-a-vis variant configurations
      const variantValue = config.variant || config['front-variant'] || config['back-variant']
      if (variantValue === 'lift') return 'wariant-lift-pic.png'
      return 'wariant-std-pic.png'
    
    case 'glass':
    case 'perk-glass':
      if (config.glass === 'single') return '1szyba.png'
      if (config.glass === 'double') return '2szyby.png'
      if (config.glass === 'triple') return '3szyby.png'
      return null
    
    case 'ceramic':
    case 'perk-ceramic':
      if (config.ceramic === 'refrabox-light') return 'ceramika-jasna-refrabox.png'
      if (config.ceramic === 'cerabox-graphite') return 'ceramika-grafitowa.png'
      return null
    
    case 'handle':
    case 'perk-handle':
      // Check if it's a lift system to use appropriate handle images
      const isLiftSystem = config.variant === 'lift' || 
                          config['front-variant'] === 'lift' || 
                          config['back-variant'] === 'lift'
      
      // For vis-a-vis models, check both front and back handles
      let handleValue = config.handle
      if (!handleValue && (config['front-handle'] || config['back-handle'])) {
        // Use front handle as primary for vis-a-vis, or back if front is not set
        handleValue = config['front-handle'] || config['back-handle']
      }
      
      if (handleValue === 'classic-black') {
        return isLiftSystem ? 'klamka-lift-classic-czarna.png' : 'klamka-classic-czarna.png'
      }
      if (handleValue === 'classic-silver') {
        return isLiftSystem ? 'klamka-lift-classic-srebrna.png' : 'klamka-classic-srebrna.png'
      }
      if (handleValue === 'design-black') return 'klamka-design-czarna.png'
      if (handleValue === 'design-black-silver') return 'klamka-design-czarno-srebrna.png'
      return null
    
    case 'bezel':
    case 'perk-bezel':
      // For vis-a-vis models, check both front and back bezels
      let bezelValue = config.bezel
      if (!bezelValue && (config['front-bezel'] || config['back-bezel'])) {
        // Use front bezel as primary for vis-a-vis, or back if front is not set
        bezelValue = config['front-bezel'] || config['back-bezel']
      }
      
      if (bezelValue === 'flush-15') return 'blenda-standard-F15.png'
      if (bezelValue === 'flush-30') return 'blenda-standard-F30.png'
      if (bezelValue === 'recessed-15') return 'blenda-standard-R15.png'
      if (bezelValue === 'recessed-30') return 'blenda-standard-R30.png'
      if (bezelValue === 'recessed-15-45') return 'blenda-standard-R45.png'
      if (bezelValue === 'recessed-lift-5') return 'blenda-lift-RL5.png'
      if (bezelValue === 'recessed-lift-15') return 'blenda-lift-RL15.png'
      if (bezelValue === 'recessed-lift-30') return 'blenda-lift-RL30.png'
      return null
    
    case 'fusion':
    case 'perk-fusion':
      return 'fusion.png'
    
    case 'aku-plus':
    case 'perk-aku-plus':
      return 'aku-plus.png'
    
    case 'aku-box':
    case 'perk-aku-box':
      return 'akubox.png'
    
    case 'easy-700':
    case 'perk-easy-700':
      return 'easy700.png'
    
    case 'cma-n':
    case 'perk-cma-n':
      return 'cma-n.png'
    
    case 'cma-s':
    case 'perk-cma-s':
      return 'cma-s.png'
    
    case 'thermal-modules':
    case 'perk-thermal-modules':
      return 'thermal-modules.png'
    
    default:
      return null
  }
}


const PreviewPanel = ({ model, config, pricing }: PreviewPanelProps) => {
  const { currentImage, imageLoaded, imageError, isLoading } = useImagePreloader(model, config)
  const modelId = (model?.id || '').toLowerCase()
  const isNemoModel = modelId.startsWith('nemo') || (model?.name ? model.name.toLowerCase().includes('nemo') : false)
  
  // Stan dla popup-a z opisami perków
  const [showPerkPopup, setShowPerkPopup] = useState(false)
  const [selectedPerkType, setSelectedPerkType] = useState<string | null>(null)
  const [selectedPerkSubtype, setSelectedPerkSubtype] = useState<string | null>(null)
  const [perkImageLoaded, setPerkImageLoaded] = useState(false)
  const [perkImageError, setPerkImageError] = useState(false)
  
  // State for preloaded images cache
  const [preloadedImages, setPreloadedImages] = useState<Map<string, boolean | string>>(new Map())

  // Preload image function - enhanced for mobile
  const preloadPerkImage = (perkType: string, perkSubtype?: string) => {
    const imageFilename = getPerkImageFilename(perkType, config)
    if (!imageFilename) return
    
    const imagePath = `images/perks/${imageFilename}`
    const cacheKey = `${perkType}-${perkSubtype || 'default'}`
    
    // Check if already preloaded
    if (preloadedImages.has(cacheKey)) return
    
    // Set as loading immediately
    setPreloadedImages(prev => new Map(prev).set(cacheKey, 'loading'))
    
    const img = new Image()
    img.onload = () => {
      setPreloadedImages(prev => new Map(prev).set(cacheKey, true))
      // If this is the currently displayed perk, update the states
      if (selectedPerkType === perkType && (selectedPerkSubtype || 'default') === (perkSubtype || 'default')) {
        setPerkImageLoaded(true)
        setPerkImageError(false)
      }
    }
    img.onerror = () => {
      setPreloadedImages(prev => new Map(prev).set(cacheKey, false))
      // If this is the currently displayed perk, update the states
      if (selectedPerkType === perkType && (selectedPerkSubtype || 'default') === (perkSubtype || 'default')) {
        setPerkImageLoaded(true)
        setPerkImageError(true)
      }
    }
    img.src = imagePath
  }

  // Auto-preload images for all visible perks when they appear on preview panel
  useEffect(() => {
    const perksData = getPerksData()
    
    // Preload images for all visible perks
    perksData.forEach(perk => {
      if (perk.variantType) {
        preloadPerkImage('variant', perk.variantType)
      } else if (perk.glassType) {
        preloadPerkImage('glass', perk.glassType)
      } else if (perk.handleType) {
        preloadPerkImage('handle', perk.handleType)
      } else if (perk.ceramicType) {
        preloadPerkImage('ceramic', perk.ceramicType)
      } else if (perk.bezelType) {
        preloadPerkImage('bezel', perk.bezelType)
      } else if (perk.akuPlusType) {
        preloadPerkImage('aku-plus', perk.akuPlusType)
      } else if (perk.thermalModulesType) {
        preloadPerkImage('thermal-modules', perk.thermalModulesType)
      } else if (perk.fusionType) {
        preloadPerkImage('fusion')
      }
    })
  }, [model, config]) // Re-run when model or config changes

  // Opisy dla wszystkich typów perków
  const perkDescriptions = {
    // Warianty
    variant: {
      standard: {
        title: "Standardowe otwieranie",
        description: "Tradycyjny system otwierania drzwiczek kominka. Drzwiczki otwierają się standardowo na zawiasach, zapewniając łatwy dostęp do komory paleniskowej. Idealny dla większości zastosowań domowych."
      },
      lift: {
        title: "System PowerLift",
        description: "Zaawansowany system automatycznego podnoszenia drzwiczek. Dzięki mechanizmowi PowerLift drzwiczki otwierają się płynnie i bez wysiłku w górę, zapewniając wygodę użytkowania i nowoczesny wygląd. Idealny do przestrzeni o ograniczonej przestrzeni przed kominkiem."
      }
    },
    
    // Szkło
    glass: {
      'single': {
        title: "Szyba pojedyncza x1",
        description: "Klasyczna pojedyncza szyba kominkowa wykonana z żaroodpornego szkła ceramicznego. Zapewnia doskonały widok na płomienie i wysoką wytrzymałość termiczną do 800°C. Standardowe rozwiązanie dla większości kominków oferujące optymalny stosunek jakości do ceny."
      },
      'double': {
        title: "Szyba podwójna x2",
        description: "Podwójna szyba kominkowa z izolacyjną komorą powietrzną pomiędzy szybami. Znacznie lepsze właściwości izolacyjne - temperatura zewnętrznej powierzchni jest o 30-40% niższa niż w przypadku pojedynczej szyby. Wyższa efektywność energetyczna i większe bezpieczeństwo użytkowania. Zalecane do nowoczesnych domów energooszczędnych."
      },
      'triple': {
        title: "Szyba potrójna x3",
        description: "Najwyższej klasy potrójna szyba kominkowa z dwoma komorami powietrznymi dla maksymalnej izolacji termicznej. Temperatura zewnętrznej powierzchni jest o 50-60% niższa niż w przypadku pojedynczej szyby. Najwyższa efektywność energetyczna i maksymalne bezpieczeństwo. Idealna do domów pasywnych, niskoenergetycznych i rodzin z małymi dziećmi."
      }
    },
    
    // Klamki
    handle: {
      'classic-black': {
        title: "Klamka Classic - czarna",
        description: "Elegancka czarna klamka w klasycznym stylu. Wykonana z wysokiej jakości materiałów odpornych na wysokie temperatury. Ponadczasowy design pasujący do każdego wnętrza."
      },
      'classic-silver': {
        title: "Klamka Classic - srebrna",
        description: "Klasyczna srebrna klamka o ponadczasowym designie. Wykonana z materiałów odpornych na korozję i wysokie temperatury. Dodaje elegancji i świeżości każdemu kominkowi."
      },
      'design-black': {
        title: "Klamka Design - czarna",
        description: "Nowoczesna czarna klamka o designerskim kształcie. Ergonomiczny uchwyt zapewniający wygodne użytkowanie. Idealna do współczesnych wnętrz o minimalistycznym charakterze."
      },
      'design-black-silver': {
        title: "Klamka Design - czarno-srebrna",
        description: "Ekskluzywna klamka łącząca czarne i srebrne wykończenia. Unikalne połączenie kolorów w nowoczesnym designie. Stanowi elegancki akcent w każdym kominkowym zestawie."
      }
    },
    
    // Ceramika
    ceramic: {
      'refrabox-light': {
        title: "Ceramika jasna (Refrabox)",
        description: "Jasna ceramika ogniotrwała Refrabox o wysokiej akumulacji ciepła. Naturalny, kremowy kolor harmonizuje z jasnymi wnętrzami. Doskonale akumuluje i oddaje ciepło długo po wygaśnięciu ognia."
      },
      'cerabox-graphite': {
        title: "Ceramika grafitowa (Cerabox)",
        description: "Ciemna ceramika grafitowa Cerabox o eleganckim, nowoczesnym wyglądzie. Doskonałe właściwości akumulacyjne i odporność na wysokie temperatury. Idealnie komponuje się z nowoczesnymi wnętrzami."
      }
    },
    
    // Blendy
    bezel: {
      'flush-15': {
        title: "Blenda płaska 15mm",
        description: "Blenda do zabudowy płaskiej o grubości 15mm. Umożliwia eleganckie wpasowanie kominka w ścianę, tworząc minimalistyczny i nowoczesny wygląd."
      },
      'flush-30': {
        title: "Blenda płaska 30mm",
        description: "Blenda do zabudowy płaskiej o grubości 30mm. Większa grubość zapewnia lepsze wykończenie i możliwość zastosowania grubszych materiałów wykończeniowych."
      },
      'recessed-15': {
        title: "Blenda wpuszczana 15mm",
        description: "Blenda wpuszczana o głębokości 15mm. Tworzy subtelną ramkę wokół kominka, dodając mu głębi i charakteru. Idealna do klasycznych aranżacji."
      },
      'recessed-30': {
        title: "Blenda wpuszczana 30mm",
        description: "Blenda wpuszczana o głębokości 30mm. Większa głębokość tworzy wyrazistą ramkę, nadając kominkowi bardziej tradycyjny i elegancki charakter."
      },
      'recessed-15-45': {
        title: "Blenda wpuszczana 15mm ze skosem 45°",
        description: "Blenda wpuszczana z elementem skośnym pod kątem 45°. Łączy nowoczesność z klasycznym designem, tworząc unikalne przejście między kominem a ścianą."
      },
      'recessed-lift-5': {
        title: "Blenda LIFT wpuszczana 5mm",
        description: "Specjalna blenda dedykowana systemowi PowerLift o głębokości 5mm. Minimalna grubość zapewniająca prawidłowe funkcjonowanie mechanizmu podnoszenia."
      },
      'recessed-lift-15': {
        title: "Blenda LIFT wpuszczana 15mm",
        description: "Blenda dedykowana systemowi PowerLift o głębokości 15mm. Zapewnia optymalne połączenie estetyki i funkcjonalności dla mechanizmu podnoszenia drzwiczek."
      },
      'recessed-lift-30': {
        title: "Blenda LIFT wpuszczana 30mm",
        description: "Blenda LIFT o głębokości 30mm. Największa dostępna głębokość dla systemu PowerLift, umożliwiająca zastosowanie grubszych materiałów wykończeniowych."
      },
      'none': {
        title: "Bez blendy",
        description: "Kominek bez dodatkowej blendy - czysty, minimalistyczny wygląd. Wkład kominkowy montowany jest bezpośrednio w przygotowanej wnęce bez dodatkowych elementów wykończeniowych."
      }
    },
    
    // FUSION
    fusion: {
      title: "System FUSION",
      description: "Innowacyjny system FUSION umożliwia rozbudowę kominka o dodatkowe MODUŁY grzewcze. Pozwala na zwiększenie mocy grzewczej i lepsze rozprowadzenie ciepła w pomieszczeniu. Idealne rozwiązanie dla większych przestrzeni."
    },
    
    // AKU+ 
    'aku-plus': {
      title: "System AKU+",
      description: "Najnowocześniejszy system akumulacji ciepła AKU+ z ulepszonymi materiałami ceramicznymi. Wydłużona akumulacja ciepła do 12 godzin po wygaśnięciu ognia. Zwiększa efektywność energetyczną kominka nawet o 40% w porównaniu do standardowych rozwiązań. Idealne dla domów ekologicznych i oszczędnych energetycznie."
    },

    // AKU BOX
    'aku-box': {
      title: "AKU BOX - WYMIENNIK STALOWO-ZASYPOWY",
      description: "Stalowo-zasypowy wymiennik akumulujący energię, obniżający temperaturę spalin i pozwalający zaoszczędzić miejsce przy kominku. Idealny do nowoczesnego, energooszczędnego budownictwa."
    },

    // EASY 700
    'easy-700': {
      title: "EASY 700 - WYMIENNIK STALOWO-SZAMOTOWY",
      description: "Stalowo-szamotowy wymiennik akumulujący energię, obniżający temperaturę spalin. Idealny do nowoczesnego, energooszczędnego budownictwa."
    },

    // CMA N
    'cma-n': {
      title: "CMA N - MODUŁ AKUMULACYJNY",
      description: "Moduł akumulacyjny CMA N o wadze 333 kg i wymiarach 112x56x35 mm. Zwiększa efektywność akumulacji ciepła, wydłużając czas oddawania ciepła po wygaśnięciu ognia."
    },

    // CMA S
    'cma-s': {
      title: "CMA S - MODUŁ AKUMULACYJNY",
      description: "Moduł akumulacyjny CMA S o wadze 338 kg i wymiarach 119x56x28 mm. Wysokiej jakości materiały ceramiczne zapewniają optymalną akumulację i dystrybucję ciepła."
    },

    // THERMAL MODULES
    'thermal-modules': {
      title: "MODUŁY TERMOAKUMULACYJNE",
      description: "Specjalne moduły termoakumulacyjne zwiększają zdolność magazynowania ciepła kominka. Wykonane z wysokiej jakości materiałów ceramicznych, wydłużają czas oddawania ciepła po wygaśnięciu ognia. Dostępne w różnych wariantach wagowych dla optymalnej akumulacji."
    }
  }

  // Funkcja obsługująca kliknięcie w dowolny perk
  const handlePerkClick = (perkType: string, perkSubtype?: string) => {
    setSelectedPerkType(perkType)
    setSelectedPerkSubtype(perkSubtype || null)
    
    // Check if image was preloaded
    const cacheKey = `${perkType}-${perkSubtype || 'default'}`
    const wasPreloaded = preloadedImages.get(cacheKey)
    
    if (wasPreloaded === true) {
      // Image was successfully preloaded
      setPerkImageLoaded(true)
      setPerkImageError(false)
    } else if (wasPreloaded === false) {
      // Image failed to preload
      setPerkImageLoaded(true) 
      setPerkImageError(true)
    } else if (wasPreloaded === 'loading') {
      // Image is being loaded right now
      setPerkImageLoaded(false)
      setPerkImageError(false)
    } else {
      // Image not preloaded, reset states
      setPerkImageLoaded(false)
      setPerkImageError(false)
    }
    
    setShowPerkPopup(true)
  }

  // Funkcja zamykająca popup
  const closePerkPopup = () => {
    setShowPerkPopup(false)
    setSelectedPerkType(null)
    setSelectedPerkSubtype(null)
    setPerkImageLoaded(false)
    setPerkImageError(false)
  }

  // Funkcja zwracająca aktualny opis
  const getCurrentDescription = () => {
    if (!selectedPerkType) return null
    
    const descriptions = perkDescriptions[selectedPerkType as keyof typeof perkDescriptions]
    
    if (selectedPerkType === 'fusion' || selectedPerkType === 'aku-plus' || selectedPerkType === 'aku-box' || selectedPerkType === 'easy-700' || selectedPerkType === 'thermal-modules') {
      const desc = descriptions as { title: string; description: string }
      
      // For FUSION, check if it's a corner model
      if (selectedPerkType === 'fusion') {
        const isCornerModel = model?.glassType === 'narozna' || model?.glassType === 'corner'
        if (isCornerModel) {
          return {
            title: "System FUSION CORNER",
            description: "Innowacyjny system FUSION CORNER umożliwia rozbudowę kominka narożnego o dodatkowe MODUŁY grzewcze. Pozwala na zwiększenie mocy grzewczej i lepsze rozprowadzenie ciepła w pomieszczeniu. Idealne rozwiązanie dla większych przestrzeni z układem narożnym."
          }
        }
      }
      
      return desc
    }
    
    if (selectedPerkSubtype && descriptions && typeof descriptions === 'object') {
      return (descriptions as any)[selectedPerkSubtype]
    }
    
    return null
  }


  // Generate perks data
  const getPerksData = (): PerkData[] => {
    if (!model) return []


    const perks: PerkData[] = []

    // NEMO thermal protection perk - first, before any other perks (applies to all NEMO models)
    if (config.nemoThermalProtection === 'with-protection') {
      perks.push({
        icon: '',
        value: 'Zabezpieczenie termiczne',
        label: '',
        active: true
      })
    }

    if (model.glassType === 'vis-a-vis') {
      // VIS-A-VIS perks - separate front and back sections
      
      // FRONT SECTION
      const frontVariant = config['front-variant'] || ''
      const frontHandleSide = config['front-handleSide'] || ''
      const frontHandle = config['front-handle'] || ''
      const frontBezel = config['front-bezel'] || ''
      
      // Front variant perk
      if (frontVariant && frontVariant !== '') {
        let frontVariantValue = ''
        
        if (frontVariant === 'standard') {
          // For standard variant, show handle side if selected
          if (frontHandleSide) {
            const sideLabel = frontHandleSide === 'right' ? 'P' : 'L'
            frontVariantValue = `Std ${sideLabel}`
          } else {
            frontVariantValue = 'Standard'
          }
        } else if (frontVariant === 'lift') {
          // For lift variant, show service handle side if selected
          // UKRYTE: klamka serwisowa
          frontVariantValue = 'LIFT'
        } else {
          frontVariantValue = 'LIFT'
        }
        
        perks.push({
          icon: '',
          value: frontVariantValue,
          label: '', // No internal label
          active: true,
          variantType: frontVariant
        })
      }
      
      // Front handle perk
      if (frontHandle && frontHandle !== '') {
        const handleLabels: { [key: string]: string } = {
          'classic-black': 'Classic',
          'classic-silver': 'Classic', 
          'design-black': 'Design',
          'design-black-silver': 'Design'
        }
        const handleLabel = handleLabels[frontHandle] || 'Classic'
        perks.push({
          icon: '',
          value: handleLabel,
          label: '', // No internal label
          active: true,
          handleType: frontHandle,
          perkVariant: frontVariant // Store variant for lift check but don't generate CSS class
        })
      }
      
      // Front bezel perk - używamy globalnej definicji symboli
      if (frontBezel && frontBezel !== '' && frontBezel !== 'none') {
        perks.push({
          icon: '',
          value: '', // Usuń tekst - tylko grafika
          label: '', // No internal label
          active: true,
          bezelType: frontBezel
        })
      }
      
      // BACK SECTION
      const backVariant = config['back-variant'] || ''
      const backHandleSide = config['back-handleSide'] || ''
      const backHandle = config['back-handle'] || ''
      const backBezel = config['back-bezel'] || ''
      
      // Back variant perk
      if (backVariant && backVariant !== '') {
        let backVariantValue = ''
        
        if (backVariant === 'standard') {
          // For standard variant, show handle side if selected
          if (backHandleSide) {
            const sideLabel = backHandleSide === 'right' ? 'P' : 'L'
            backVariantValue = `Std ${sideLabel}`
          } else {
            backVariantValue = 'Standard'
          }
        } else if (backVariant === 'lift') {
          // For lift variant, show service handle side if selected
          // UKRYTE: klamka serwisowa
          backVariantValue = 'LIFT'
        } else {
          backVariantValue = 'LIFT'
        }
        
        perks.push({
          icon: '',
          value: backVariantValue,
          label: '', // No internal label
          active: true,
          variantType: backVariant
        })
      }
      
      // Back handle perk
      if (backHandle && backHandle !== '') {
        const handleLabels: { [key: string]: string } = {
          'classic-black': 'Classic',
          'classic-silver': 'Classic', 
          'design-black': 'Design',
          'design-black-silver': 'Design'
        }
        const handleLabel = handleLabels[backHandle] || 'Classic'
        perks.push({
          icon: '',
          value: handleLabel,
          label: '', // No internal label
          active: true,
          handleType: backHandle,
          perkVariant: backVariant // Store variant for lift check but don't generate CSS class
        })
      }
      
      // Back bezel perk - używamy globalnej definicji symboli
      if (backBezel && backBezel !== '' && backBezel !== 'none') {
        perks.push({
          icon: '',
          value: '', // Usuń tekst - tylko grafika
          label: '', // No internal label
          active: true,
          bezelType: backBezel
        })
      }
    } else {
      // Standard variant perk - only show when variant is selected
      if (config.variant && config.variant !== '') {
        let variantValue = ''
        
        if (config.variant === 'standard') {
          // For standard variant, show handle side if selected
          if (config.handleSide) {
            const sideLabel = config.handleSide === 'right' ? 'P' : 'L'
            variantValue = `Standard ${sideLabel}`
          } else {
            variantValue = 'Standard'
          }
        } else if (config.variant === 'lift') {
          // For lift variant, show service handle side if selected
          // UKRYTE: klamka serwisowa
          variantValue = 'LIFT'
        } else {
          variantValue = config.variant || 'LIFT'
        }
        
        perks.push({
          icon: '',
          value: variantValue,
          label: '', // No internal label
          active: true,
          variantType: config.variant
        })
      }
    }


    // Glass perk - only show if glass is selected and not empty
    if (config.glass && config.glass !== '') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika
        label: '',
        active: true,
        glassType: config.glass
      })
    }


    // Ceramic perk - only show if ceramic is selected and not empty
    if (config.ceramic && config.ceramic !== '') {
      perks.push({
        icon: '',
        value: config.ceramic === 'refrabox-light' ? 'Jasna' : 'Grafitowa',
        label: '', // No internal label
        active: true,
        ceramicType: config.ceramic
      })
    }


    // Handle perk - only show if handle is selected and not empty
    if (config.handle && config.handle !== '') {
      const handleLabels: { [key: string]: string } = {
        'classic-black': 'Classic',
        'classic-silver': 'Classic', 
        'design-black': 'Design',
        'design-black-silver': 'Design'
      }
      const handleLabel = handleLabels[config.handle] || 'Classic'
      perks.push({
        icon: '',
        value: handleLabel,
        label: '', // No internal label
        active: true,
        handleType: config.handle
      })
    }


    if (model.glassType === 'vis-a-vis') {
      // For vis-a-vis models, don't add a combined bezel perk
      // Individual front/back bezels are handled in external labels
    } else {
      // Only add bezel perk if bezel is selected AND not 'none' - używamy globalnej definicji
      if (config.bezel && config.bezel !== 'none' && config.bezel !== '') {
        perks.push({
          icon: '',
          value: '', // Usuń tekst - tylko grafika
          label: '', // No internal label
          active: true,
          bezelType: config.bezel
        })
      }
    }


    // FUSION perk - only if available and actually selected
    if (isFusionAvailable(model)) {
      const hasFusion = config.fusionBasic !== 'none' || config.fusionModules !== 'none'
      if (hasFusion) {
        // Calculate FUSION display value
        let fusionValue = 'TAK'
        
        // If modules are selected, show count
        if (config.fusionModules === 'fusion-modules' && config.fusionModulesCount > 0) {
          fusionValue = `TAK + ${config.fusionModulesCount}`
        }
        
        perks.push({
          icon: '', // Empty icon - using CSS background image instead
          value: fusionValue,
          label: '', // No internal label
          // color: '#ff4500', // Removed - using CSS background image instead
          fusionType: 'fusion', // Add fusion type for CSS class
          active: true
        })
      }
    }

    // FUSION CORNER perk - only if available and actually selected
    const variant = config.variant === 'lift' ? 'lift' : 'standard'
    if (isFusionCornerAvailable(model, variant)) {
      const hasFusionCorner = config.fusionCorner !== 'none' || config.fusionCornerModules !== 'none'
      if (hasFusionCorner) {
        // Calculate FUSION CORNER display value
        let fusionCornerValue = 'TAK'
        
        // If modules are selected, show count
        if (config.fusionCornerModules === 'fusion-corner-modules' && config.fusionCornerModulesCount > 0) {
          fusionCornerValue = `TAK + ${config.fusionCornerModulesCount}`
        }
        
        perks.push({
          icon: '', // Empty icon - using CSS background image instead
          value: fusionCornerValue,
          label: '', // No internal label
          fusionType: 'fusion-corner', // Add fusion corner type for CSS class
          active: true
        })
      }
    }


    // AKU+ perk - only if available and active
    if (isAkuPlusAvailable(model)) {
      const hasAkuPlus = config.akuPlus !== 'none'
      if (hasAkuPlus) {  // Only show perk when AKU+ is actually selected
        perks.push({
          icon: '',
          value: '', // Usuń tekst - tylko grafika
          label: '', // No internal label
          active: true,
          akuPlusType: 'aku-plus' // Add type for CSS class generation
        })
      }
    }

    // MATERIAŁY AKUMULACYJNE perks - only if selected
    if (config.akuBox && config.akuBox !== 'none') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika
        label: '', // No internal label
        active: true,
        accumulationMaterialType: 'aku-box' // Add type for CSS class generation
      })
    }

    if (!isNemoModel && config.easy700 && config.easy700 !== 'none') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika
        label: '', // No internal label
        active: true,
        accumulationMaterialType: 'easy-700' // Add type for CSS class generation
      })
    }

    if (config.cmaN && config.cmaN !== 'none') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika
        label: '', // No internal label
        active: true,
        accumulationMaterialType: 'cma-n' // Add type for CSS class generation
      })
    }

    if (config.cmaS && config.cmaS !== 'none') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika
        label: '', // No internal label
        active: true,
        accumulationMaterialType: 'cma-s' // Add type for CSS class generation
      })
    }

    // THERMAL MODULES perk - only if available and selected
    if (config.thermalModules && config.thermalModules !== 'none') {
      perks.push({
        icon: '',
        value: '', // Usuń tekst - tylko grafika  
        label: '', // No internal label
        active: true,
        thermalModulesType: config.thermalModules // Add type for CSS class generation
      })
    }

    // THERMAL HANGERS perk - only if available and selected
    if (config.thermalHangers && config.thermalHangers === 'yes') {
      perks.push({
        icon: '🔗',
        value: '',
        label: 'Zawiesia kpl.',
        active: true
      })
    }

    // CONTROLLERS perk - only if selected and not 'none'
    if (config.controller && config.controller !== 'none') {
      const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller;
      const isNemo = model?.id?.startsWith('nemo-');
      
      let displayLabel: string;
      if (isNemo) {
        // Użyj funkcji pomocniczej dla NEMO
        displayLabel = getNemoControllerFullLabel(
          config.controller,
          config.controllerColor || 'none',
          config.controllerBuffor || false
        );
      } else {
        // Dragon logic
        const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb';
        displayLabel = controllerLabel;
        if (requiresColor && config.controllerColor && config.controllerColor !== 'none') {
          const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK';
          displayLabel = controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel;
        }
      }
      
      perks.push({
        icon: '🎮',
        value: '',
        label: displayLabel,
        active: true
      })
    }

    // DOOR SENSOR perk - only if selected
    if (config.doorSensor === 'czujnik-drzwiczek') {
      const doorSensorLabel = model?.labels?.controllers?.['czujnik-drzwiczek'] || 'Czujnik drzwiczek';
      perks.push({
        icon: '🚪',
        value: '',
        label: doorSensorLabel,
        active: true
      })
    }


    return perks
  }


  // Generate external labels for specific perks
  const getExternalLabels = (): { [index: number]: string } => {
    if (!model) return {}
    
    const labels: { [index: number]: string } = {}
    let currentIndex = 0
    
    if (model.glassType === 'vis-a-vis') {
      // VIS-A-VIS labels - separate for front and back
      
      // FRONT SECTION
      const frontVariant = config['front-variant'] || ''
      const frontHandle = config['front-handle'] || ''
      const frontBezel = config['front-bezel'] || ''
      
      if (frontVariant && frontVariant !== '') {
        labels[currentIndex++] = 'Przód - Wariant'
      }
      
      if (frontHandle && frontHandle !== '') {
        let frontHandleLabel = 'Przód - Klamka'
        const frontHandleSide = config['front-handleSide']
        if (frontHandleSide === 'left' || frontHandleSide === 'right') {
          const sideLabel = frontHandleSide === 'right' ? ' (P)' : ' (L)'
          frontHandleLabel += sideLabel
        }
        labels[currentIndex++] = frontHandleLabel
      }
      
      if (frontBezel && frontBezel !== 'none' && frontBezel !== '') {
        labels[currentIndex++] = 'Przód - Blenda'
      }
      
      // BACK SECTION
      const backVariant = config['back-variant'] || ''
      const backHandle = config['back-handle'] || ''
      const backBezel = config['back-bezel'] || ''
      
      if (backVariant && backVariant !== '') {
        labels[currentIndex++] = 'Tył - Wariant'
      }
      
      if (backHandle && backHandle !== '') {
        let backHandleLabel = 'Tył - Klamka'
        const backHandleSide = config['back-handleSide']
        if (backHandleSide === 'left' || backHandleSide === 'right') {
          const sideLabel = backHandleSide === 'right' ? ' (P)' : ' (L)'
          backHandleLabel += sideLabel
        }
        labels[currentIndex++] = backHandleLabel
      }
      
      if (backBezel && backBezel !== 'none' && backBezel !== '') {
        labels[currentIndex++] = 'Tył - Blenda'
      }
      
      // Add common labels for vis-a-vis models too (Glass, Ceramic)
      // 7. Glass perk - only if selected
      if (config.glass && config.glass !== '') {
        labels[currentIndex++] = 'Szyba'
      }
      
      // 8. Ceramic perk - only if selected
      if (config.ceramic && config.ceramic !== '') {
        labels[currentIndex++] = 'Ceramika'
      }
      
      // 9. FUSION perk - only if available and selected
      if (isFusionAvailable(model)) {
        const hasFusion = config.fusionBasic !== 'none' || config.fusionModules !== 'none'
        if (hasFusion) {
          let fusionLabel = 'FUSION'
          
          // If modules are selected, show count in label
          if (config.fusionModules === 'fusion-modules' && config.fusionModulesCount > 0) {
            fusionLabel = `FUSION + ${config.fusionModulesCount}`
          }
          
          labels[currentIndex++] = fusionLabel
        }
      }

      // 9b. FUSION CORNER perk - only if available and selected
      const variant = config.variant === 'lift' ? 'lift' : 'standard'
      if (isFusionCornerAvailable(model, variant)) {
        const hasFusionCorner = config.fusionCorner !== 'none' || config.fusionCornerModules !== 'none'
        if (hasFusionCorner) {
          let fusionCornerLabel = 'FUSION CORNER'
          
          // If modules are selected, show count in label
          if (config.fusionCornerModules === 'fusion-corner-modules' && config.fusionCornerModulesCount > 0) {
            fusionCornerLabel = `FUSION CORNER + ${config.fusionCornerModulesCount}`
          }
          
          labels[currentIndex++] = fusionCornerLabel
        }
      }


      // 10. AKU+ perk - only if available and active
      if (isAkuPlusAvailable(model)) {
        const hasAkuPlus = config.akuPlus !== 'none'
        if (hasAkuPlus) {
          labels[currentIndex++] = 'AKU+'
        }
      }

      // 10b. NEMO thermal protection - show only when with-protection is selected
      if (config.nemoThermalProtection === 'with-protection') {
        labels[currentIndex++] = 'Zabezpieczenie termiczne: TAK'
      }

      // 11. MATERIAŁY AKUMULACYJNE perks dla vis-a-vis
      if (config.akuBox && config.akuBox !== 'none') {
        labels[currentIndex++] = 'AKU BOX'
      }
      
      if (!isNemoModel && config.easy700 && config.easy700 !== 'none') {
        labels[currentIndex++] = 'EASY 700'
      }
      
      if (config.cmaN && config.cmaN !== 'none') {
        labels[currentIndex++] = 'CMA N'
      }
      
      if (config.cmaS && config.cmaS !== 'none') {
        labels[currentIndex++] = 'CMA S'
      }

      // 12. THERMAL MODULES perk dla vis-a-vis
      if (config.thermalModules && config.thermalModules !== 'none') {
        // For perk display, show Tak with weight based on model series
        const isDragon8Series = model?.id?.includes('dragon-8') || false;
        const weight = isDragon8Series ? '21 kg' : '34,5 kg';
        const shortThermalLabel = `Tak (${weight})`;
        labels[currentIndex++] = shortThermalLabel
      }
    } else {
      // Standard model labels - MUSI być w tej samej kolejności co perki!
      if (config.variant && config.variant !== '') {
        labels[currentIndex++] = 'Wariant'
      }
      // Glass perk - only if selected
      if (config.glass && config.glass !== '') {
        labels[currentIndex++] = 'Szyba'
      }
      // Strona wkładu – for standard and LIFT corner Dragon/Nemo models when selected
      if ((config.variant === 'lift' || config.variant === 'standard') && model?.glassType === 'narozna' && 
          (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) &&
          (config.sideGlass === 'left' || config.sideGlass === 'right')) {
        const sideLabel = config.sideGlass === 'right' ? ' (P)' : ' (L)'
        labels[currentIndex++] = 'Strona wkładu' + sideLabel
      }
      // Ceramic perk - only if selected
      if (config.ceramic && config.ceramic !== '') {
        labels[currentIndex++] = 'Ceramika'
      }
      // Handle perk - only if selected
      if (config.handle && config.handle !== '') {
        let handleLabel = 'Klamka'
        if (config.variant === 'standard' && (config.handleSide === 'left' || config.handleSide === 'right')) {
          const sideLabel = config.handleSide === 'right' ? ' (P)' : ' (L)'
          handleLabel += sideLabel
        }
        labels[currentIndex++] = handleLabel
      }
      // Bezel perk - only if selected and not 'none'
      if (config.bezel && config.bezel !== 'none' && config.bezel !== '') {
        labels[currentIndex++] = 'Blenda'
      }
      // FUSION perk - only if available and selected
      if (isFusionAvailable(model)) {
        const hasFusion = config.fusionBasic !== 'none' || config.fusionModules !== 'none'
        if (hasFusion) {
          let fusionLabel = 'FUSION'
          // If modules are selected, show count in label
          if (config.fusionModules === 'fusion-modules' && config.fusionModulesCount > 0) {
            fusionLabel = `FUSION + ${config.fusionModulesCount}`
          }
          labels[currentIndex++] = fusionLabel
        }
      }
      
      // FUSION CORNER perk - only if available and selected  
      const variant = config.variant === 'lift' ? 'lift' : 'standard'
      if (isFusionCornerAvailable(model, variant)) {
        const hasFusionCorner = config.fusionCorner !== 'none' || config.fusionCornerModules !== 'none'
        if (hasFusionCorner) {
          let fusionCornerLabel = 'FUSION CORNER'
          // If modules are selected, show count in label
          if (config.fusionCornerModules === 'fusion-corner-modules' && config.fusionCornerModulesCount > 0) {
            fusionCornerLabel = `FUSION CORNER + ${config.fusionCornerModulesCount}`
          }
          labels[currentIndex++] = fusionCornerLabel
        }
      }
      // AKU+ perk - only if available and active
      if (isAkuPlusAvailable(model)) {
        const hasAkuPlus = config.akuPlus !== 'none'
        if (hasAkuPlus) {
          labels[currentIndex++] = 'AKU+'
        }
      }

      // NEMO thermal protection - show only when with-protection is selected
      if (config.nemoThermalProtection === 'with-protection') {
        labels[currentIndex++] = 'Zabezpieczenie termiczne: TAK'
      }
    }
    
    // MATERIAŁY AKUMULACYJNE perks - dostępne dla wszystkich modeli
    if (config.akuBox && config.akuBox !== 'none') {
      labels[currentIndex++] = 'AKU BOX'
    }
    
    if (!isNemoModel && config.easy700 && config.easy700 !== 'none') {
      labels[currentIndex++] = 'EASY 700'
    }
    
    if (config.cmaN && config.cmaN !== 'none') {
      labels[currentIndex++] = 'CMA N'
    }
    
    if (config.cmaS && config.cmaS !== 'none') {
      labels[currentIndex++] = 'CMA S'
    }

    // THERMAL MODULES perk - dostępne dla wszystkich modeli
    if (config.thermalModules && config.thermalModules !== 'none') {
      // For perk display, show Tak with weight based on model series
      const isDragon8Series = model?.id?.includes('dragon-8') || false;
      const weight = isDragon8Series ? '21 kg' : '34,5 kg';
      const shortThermalLabel = `Tak (${weight})`;
      labels[currentIndex++] = shortThermalLabel
    }
    
    // THERMAL HANGERS perk - dostępne dla wybranych modeli
    if (config.thermalHangers && config.thermalHangers === 'yes') {
      labels[currentIndex++] = 'Zawiesia kpl.'
    }
    
    // UWAGA: NIE dodajemy już FUSION i AKU+ na końcu - są już dodane w odpowiednich sekcjach!


    return labels
  }


  // Generate summary data
  const getSummaryData = () => {
    if (!model) return []


    const items = []

    // Check if LIFT variant is selected (for both standard and vis-a-vis models)
    const isLiftVariant = model.glassType === 'vis-a-vis' 
      ? (config['front-variant'] === 'lift' || config['back-variant'] === 'lift')
      : config.variant === 'lift';
    
    const modelNameWithLift = isLiftVariant 
      ? `${model.name} LIFT`
      : model.name;

    // 1. Model
    items.push({ label: 'Model', value: modelNameWithLift })
    
    // 2. Typ
    items.push({ 
      label: 'Typ', 
      value: model.type.includes('powietrzny') ? 'Powietrzny' : 'Wodny' 
    })

    // 3. Wariant
    if (model.glassType === 'vis-a-vis') {
      const frontVariant = config['front-variant']
      const backVariant = config['back-variant']
      
      if (!frontVariant || !backVariant) {
        items.push({ 
          label: 'Wariant', 
          value: '-' 
        })
      } else if (frontVariant === backVariant) {
        items.push({ 
          label: 'Wariant', 
          value: frontVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT' 
        })
      } else {
        const frontLabel = frontVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
        const backLabel = backVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
        items.push({ 
          label: 'Wariant', 
          value: `Przód: ${frontLabel}, Tył: ${backLabel}` 
        })
      }
    } else {
      const variant = config.variant
      items.push({ 
        label: 'Wariant', 
        value: variant ? (variant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT') : '-'
      })
    }

    // 4. Konfiguracja szyby
    // Use model labels if available, otherwise fallback to default labels
    const glassLabel = config.glass && config.glass !== '' 
      ? (model?.labels?.glass?.[config.glass] || 'Szyba pojedyncza')
      : '-'
    items.push({ 
      label: 'Konfiguracja szyby', 
      value: glassLabel
    })
    
    // 5. Strona wkładu - for standard and LIFT corner Dragon/Nemo models when selected
    if ((config.variant === 'lift' || config.variant === 'standard') && model?.glassType === 'narozna' && 
        (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) &&
        (config.sideGlass === 'left' || config.sideGlass === 'right')) {
      const sideValue = config.sideGlass === 'right' ? 'Prawa' : 'Lewa'
      items.push({ 
        label: 'Strona wkładu', 
        value: sideValue
      })
    }
    
    // 6. Ceramika ognioodporna
    items.push({ 
      label: 'Ceramika ognioodporna', 
      value: config.ceramic && config.ceramic !== '' ? (model?.labels?.ceramic?.[config.ceramic] || 'Ceramika jasna (REFRABOX Light)') : '-'
    })


    // 7. Klamka
    
    if (model.glassType === 'vis-a-vis') {
      const frontHandle = config['front-handle']
      const backHandle = config['back-handle']
      
      if (!frontHandle || frontHandle === '' || !backHandle || backHandle === '') {
        items.push({ 
          label: 'Klamka', 
          value: '-' 
        })
      } else if (frontHandle === backHandle) {
        const handleName = model?.labels?.handle?.[frontHandle] || 'Klamka Classic - czarna'
        items.push({ 
          label: 'Klamka', 
          value: handleName
        })
      } else {
        const frontHandleName = model?.labels?.handle?.[frontHandle] || 'Klamka Classic - czarna'
        const backHandleName = model?.labels?.handle?.[backHandle] || 'Klamka Classic - czarna'
        items.push({ 
          label: 'Klamka', 
          value: `Przód: ${frontHandleName}, Tył: ${backHandleName}` 
        })
      }
      
      // 8. Strona klamki - show for vis-a-vis, prioritizing front
      const frontVariant = config['front-variant']
      const backVariant = config['back-variant']
      const frontHandleSide = config['front-handleSide']
      const backHandleSide = config['back-handleSide']
      // Determine what to show first based on front variant
      const hasFrontStandard = frontVariant === 'standard'
      const hasBackStandard = backVariant === 'standard'
      
      // Show front first (standard or lift depending on what's selected)
      if (hasFrontStandard && frontHandleSide) {
        items.push({ 
          label: 'Strona klamki (Przód)', 
          value: frontHandleSide === 'right' ? 'Prawa' : 'Lewa'
        })
      }
      // }
      
      // Then show back (standard or lift depending on what's selected)
      if (hasBackStandard && backHandleSide) {
        items.push({ 
          label: 'Strona klamki (Tył)', 
          value: backHandleSide === 'right' ? 'Prawa' : 'Lewa'
        })
      }
    } else {
      // Standard models - without side label in handle name
      if (config.handle && config.handle !== '') {
        const handleName = model?.labels?.handle?.[config.handle] || 'Klamka Classic - czarna'
        items.push({ 
          label: 'Klamka', 
          value: handleName
        })
      } else {
        items.push({ 
          label: 'Klamka', 
          value: '-'
        })
      }
      
      // 8. Strona klamki - only show for standard variant
      const variant = config.variant
      if (variant === 'standard') {
        const handleSide = config.handleSide
        let handleSideValue = handleSide ? (handleSide === 'right' ? 'Prawa' : handleSide === 'left' ? 'Lewa' : '-') : '-'
        items.push({ 
          label: 'Strona klamki', 
          value: handleSideValue
        })
      }

    }


    // 9. Blenda
    const bezelLabels: { [key: string]: string } = {
      'none': 'Bez blendy',
      'standard': 'Blenda standardowa',
      'wide': 'Blenda szeroka',
      'flush-15': 'Licująca 15mm (R15)',
      'flush-30': 'Licująca 30mm (R30)',
      'recessed-15': 'Zagłębiająca 15mm (RZ15)',
      'recessed-30': 'Zagłębiająca 30mm (RZ30)',
      'recessed-15-45': 'Zagłębiająca 15mm 45° (RZ45)',
      'recessed-lift-5': 'LIFT 5mm (RL05)',
      'recessed-lift-15': 'LIFT 15mm (RL15)',
      'recessed-lift-30': 'LIFT 30mm (RL30)'
    }
    
    if (model.glassType === 'vis-a-vis') {
      const frontBezel = config['front-bezel']
      const backBezel = config['back-bezel']
      
      if (!frontBezel || frontBezel === '' || !backBezel || backBezel === '') {
        items.push({ 
          label: 'Blenda', 
          value: '-' 
        })
      } else if (frontBezel === backBezel) {
        items.push({ 
          label: 'Blenda', 
          value: bezelLabels[frontBezel] || frontBezel 
        })
      } else {
        items.push({ 
          label: 'Blenda', 
          value: `Przód: ${bezelLabels[frontBezel] || frontBezel}, Tył: ${bezelLabels[backBezel] || backBezel}` 
        })
      }
    } else {
      items.push({ 
        label: 'Blenda', 
        value: config.bezel && config.bezel !== '' ? (bezelLabels[config.bezel] || config.bezel) : '-'
      })
    }


    // 10. FUSION - only if available and selected
    if (isFusionAvailable(model)) {
      const fusionParts = []
      if (config.fusionBasic && config.fusionBasic !== 'none') {
        fusionParts.push('Tak')
      }
      if (config.fusionModules && config.fusionModules !== 'none') {
        fusionParts.push(`MODUŁY dodatkowe: ${config.fusionModulesCount}`)
      }
      
      // Only show FUSION if it's actually selected
      if (fusionParts.length > 0) {
        items.push({ 
          label: 'FUSION', 
          value: fusionParts.join(', ')
        })
      }
    }

    // 10b. FUSION CORNER - only if available and selected
    const variant = config.variant === 'lift' ? 'lift' : 'standard'
    if (isFusionCornerAvailable(model, variant)) {
      const fusionCornerParts = []
      if (config.fusionCorner && config.fusionCorner !== 'none') {
        fusionCornerParts.push('Tak')
      }
      if (config.fusionCornerModules && config.fusionCornerModules !== 'none') {
        fusionCornerParts.push(`MODUŁY dodatkowe: ${config.fusionCornerModulesCount}`)
      }
      
      // Only show FUSION CORNER if it's actually selected
      if (fusionCornerParts.length > 0) {
        items.push({ 
          label: 'FUSION CORNER', 
          value: fusionCornerParts.join(', ')
        })
      }
    }

    // 11. AKU+ - only if available and selected
    if (isAkuPlusAvailable(model)) {
      if (config.akuPlus && config.akuPlus !== 'none') {
        items.push({ 
          label: 'AKU+', 
          value: 'Tak'
        })
      }
    }

    // 11b. NEMO thermal protection - show only when with-protection is selected
    if (config.nemoThermalProtection === 'with-protection') {
      items.push({ 
        label: 'Zabezpieczenie termiczne', 
        value: 'Tak'
      })
    }

    // 12. AKU BOX - show only if selected
    if (config.akuBox && config.akuBox !== 'none') {
      items.push({ 
        label: 'AKU BOX', 
        value: 'Tak'
      })
    }

    // 13. EASY 700 - show only if selected
    if (!isNemoModel && config.easy700 && config.easy700 !== 'none') {
      items.push({ 
        label: 'EASY 700', 
        value: 'Tak'
      })
    }

    // 13b. CMA N - show only if selected
    if (config.cmaN && config.cmaN !== 'none') {
      items.push({ 
        label: 'CMA N', 
        value: 'Tak'
      })
    }

    // 13c. CMA S - show only if selected
    if (config.cmaS && config.cmaS !== 'none') {
      items.push({ 
        label: 'CMA S', 
        value: 'Tak'
      })
    }

    // 14. MODUŁY TERMOAKUMULACYJNE - only if selected
    if (config.thermalModules && config.thermalModules !== 'none') {
      // For display, show count with weight based on model series
      const isDragon8Series = model?.id?.includes('dragon-8') || false;
      const weight = isDragon8Series ? '21 kg' : '34,5 kg';
      const count = config.thermalModulesCount || 1;
      const shortThermalLabel = count > 1 ? `${count} szt. (${weight} każdy)` : `1 szt. (${weight})`;
      items.push({ 
        label: 'MODUŁY\nTERMOAKUMULACYJNE', 
        value: shortThermalLabel
      })
    }

    // 15. ZAWIESIA TERMOAKUMULACYJNE - only if selected
    if (config.thermalHangers && config.thermalHangers === 'yes') {
      items.push({ 
        label: 'Zawiesia kpl.', 
        value: 'TAK'
      })
    }

    // 16. STEROWNIKI - only if selected and not 'none'
    if (config.controller && config.controller !== 'none') {
      const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller;
      const isNemo = model?.id?.startsWith('nemo-');
      
      let displayValue: string;
      if (isNemo) {
        // Użyj funkcji pomocniczej dla NEMO
        displayValue = getNemoControllerFullLabel(
          config.controller,
          config.controllerColor || 'none',
          config.controllerBuffor || false
        );
      } else {
        // Dragon logic
        const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb';
        displayValue = controllerLabel;
        if (requiresColor && config.controllerColor && config.controllerColor !== 'none') {
          const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK';
          displayValue = controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel;
        }
      }
      
      items.push({ 
        label: 'STEROWNIK', 
        value: displayValue
      })
    }

    // 17. CZUJNIK DRZWICZEK - only if selected
    if (config.doorSensor === 'czujnik-drzwiczek') {
      items.push({ 
        label: 'Czujnik drzwiczek', 
        value: 'Tak'
      })
    }

    return items
  }


  if (!model) {
    return (
      <aside className="preview">
        <h3>Podgląd</h3>
        <div className="no-model-placeholder" id="no-model-placeholder">
          <div className="placeholder-content">
            <img 
              src="images/unico-logo.png" 
              alt="UNICO logo" 
              className="placeholder-logo"
            />
            <p>Wybierz model wkładu aby rozpocząć konfigurację</p>
          </div>
        </div>
      </aside>
    )
  }


  const perksData = getPerksData()
  const summaryData = getSummaryData()
  const externalLabels = getExternalLabels()

  // Check if we need to mirror the image (left side selected)
  const shouldMirrorImage = () => {
    // For vis-a-vis models - check ONLY front handle side when front variant is standard
    if (model.glassType === 'vis-a-vis') {
      const frontVariant = config['front-variant']
      const frontHandleSide = config['front-handleSide']
      
      // Mirror only if front is standard AND front has LEFT handle
      if (frontVariant === 'standard' && frontHandleSide === 'left') {
        return true
      }
    }
    
    // For corner models with LIFT - check side glass position (LEFT = mirror)
    if (model.glassType === 'narozna' && config.variant === 'lift') {
      if (config.sideGlass === 'left') {
        return true
      }
    }
    
    // For standard models (non-corner) - check handle side (LEFT = mirror)
    if (model.glassType !== 'vis-a-vis' && model.glassType !== 'narozna') {
      if (config.handleSide === 'left') {
        return true
      }
    }
    
    // For corner models without LIFT (standard) - check handle side (RIGHT = mirror)
    if (model.glassType === 'narozna' && config.variant !== 'lift') {
      if (config.handleSide === 'right') {
        return true
      }
    }
    
    return false
  }

  const mirrorImage = shouldMirrorImage()


  return (
    <aside className="preview">
      <h3>Podgląd</h3>
      
      {imageError || !currentImage ? (
        <div className="no-model-placeholder" id="no-model-placeholder">
          <div className="placeholder-content">
            <img 
              src="images/unico-logo.png" 
              alt="UNICO logo" 
              className="placeholder-logo"
            />
            <p>Brak wizualizacji</p>
          </div>
        </div>
      ) : (
        <div className="fireplace-visual" id="fireplace-visual" style={{
          transform: mirrorImage ? 'scaleX(-1)' : 'none'
        }}>
          {isLoading && (
            <div className="image-loading-spinner">
              <div className="spinner"></div>
              <p>Ładowanie wizualizacji...</p>
            </div>
          )}
          <img 
            id="model-image"
            src={currentImage} 
            alt={model.name}
            className={`fireplace-img ${imageLoaded ? 'loaded' : 'loading'}`}
            style={{ 
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          />
        </div>
      )}


      <div className="preview-content" id="preview-content">
        {/* Perks Container - TYMCZASOWO UKRYTE */}
        <div className="perks-container" style={{ display: 'none' }}>
          {perksData.map((perk, index) => (
            <div key={index}>
              <div 
                id={getPerkId(perk, index)}
                className={`perk ${perk.active ? 'active' : ''} ${perk.glassType ? 'glass-perk' : ''} ${perk.handleType ? 'handle-perk' : ''} ${perk.ceramicType ? 'ceramic-perk' : ''} ${perk.variantType ? 'variant-perk' : ''} ${perk.bezelType ? 'bezel-perk' : ''} ${perk.akuPlusType ? 'aku-plus-perk' : ''} ${perk.thermalModulesType ? 'thermal-modules-perk' : ''}`}
                onMouseEnter={() => {
                  // Preload image on hover
                  if (perk.variantType) {
                    preloadPerkImage('variant', perk.variantType)
                  } else if (perk.glassType) {
                    preloadPerkImage('glass', perk.glassType)
                  } else if (perk.handleType) {
                    preloadPerkImage('handle', perk.handleType)
                  } else if (perk.ceramicType) {
                    preloadPerkImage('ceramic', perk.ceramicType)
                  } else if (perk.bezelType) {
                    preloadPerkImage('bezel', perk.bezelType)
                  } else if (perk.akuPlusType) {
                    preloadPerkImage('aku-plus', perk.akuPlusType)
                  } else if (perk.thermalModulesType) {
                    preloadPerkImage('thermal-modules', perk.thermalModulesType)
                  } else if (perk.accumulationMaterialType) {
                    preloadPerkImage(perk.accumulationMaterialType, perk.accumulationMaterialType)
                  } else if (perk.fusionType) {
                    preloadPerkImage('fusion')
                  }
                }}
                onClick={() => {
                  if (perk.variantType) {
                    handlePerkClick('variant', perk.variantType)
                  } else if (perk.glassType) {
                    handlePerkClick('glass', perk.glassType)
                  } else if (perk.handleType) {
                    handlePerkClick('handle', perk.handleType)
                  } else if (perk.ceramicType) {
                    handlePerkClick('ceramic', perk.ceramicType)
                  } else if (perk.bezelType) {
                    handlePerkClick('bezel', perk.bezelType)
                  } else if (perk.akuPlusType) {
                    handlePerkClick('aku-plus', perk.akuPlusType)
                  } else if (perk.thermalModulesType) {
                    handlePerkClick('thermal-modules', perk.thermalModulesType)
                  } else if (perk.accumulationMaterialType) {
                    handlePerkClick(perk.accumulationMaterialType, perk.accumulationMaterialType)
                  } else if (perk.fusionType) {
                    handlePerkClick('fusion')
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div 
                  className={`perk-icon ${perk.glassType ? `glass-${perk.glassType}` : ''} ${perk.handleType ? (() => {
                    // For vis-a-vis models, check individual perk variant; for regular models, check global variant
                    const isLiftForThisPerk = model.glassType === 'vis-a-vis' 
                      ? perk.perkVariant === 'lift'  // For vis-a-vis: check this specific perk's stored variant
                      : config.variant === 'lift'    // For regular models: check global variant
                    
                    return isLiftForThisPerk && (perk.handleType === 'classic-black' || perk.handleType === 'classic-silver') 
                      ? `handle-lift-${perk.handleType}` 
                      : `handle-${perk.handleType}`
                  })() : ''} ${perk.ceramicType ? `ceramic-${perk.ceramicType}` : ''} ${perk.variantType ? `variant-${perk.variantType}` : ''} ${perk.bezelType ? `bezel-${perk.bezelType}` : ''} ${perk.fusionType ? perk.fusionType : ''} ${perk.akuPlusType ? `${perk.akuPlusType}` : ''} ${perk.thermalModulesType ? `thermal-modules` : ''} ${perk.accumulationMaterialType ? `${perk.accumulationMaterialType}` : ''}`}
                  style={perk.color ? { background: perk.color } : {}}
                >
                  {perk.icon}
                </div>
                <div className="perk-value">{perk.value}</div>
                <div className="perk-label">{perk.label}</div>
              </div>
              {externalLabels[index] && (
                <div className="perk-external-label">
                  {externalLabels[index]}
                </div>
              )}
            </div>
          ))}
        </div>


        <div className="summary">
          <h4>Twoja konfiguracja</h4>
          <table>
            <tbody>
              {summaryData.map((item, index) => {
                const isThermalModules = item.label.includes('MODUŁY') && item.label.includes('TERMOAKUMULACYJNE');
                
                if (isThermalModules) {
                  return (
                    <tr key={index} className="thermal-modules-row">
                      <td>MODUŁY TERMOAKUMULACYJNE:</td>
                      <td className="no-break">{extractValue(item.value)}</td>
                    </tr>
                  );
                }
                
                return (
                  <tr key={index}>
                    <td>{item.label}:</td>
                    <td className={item.value.includes('Tak (') ? 'no-break' : ''}>{extractValue(item.value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          <div className="price" id="total-price">
            {formatPrice(pricing.totalPrice)} zł
          </div>
        </div>


        <div className="spec-table" id="tech-specs">
          <h4>Dane techniczne</h4>
          <table>
            <tbody>
              <tr><td>Moc grzewcza:</td><td>{model.specs.power}</td></tr>
              {model.specs.efficiency && <tr><td>Sprawność:</td><td>{model.specs.efficiency}</td></tr>}
              {model.specs.energyClass && <tr><td>Klasa energetyczna:</td><td>{model.specs.energyClass}</td></tr>}
              {model.specs.waterHeaterPower && <tr><td>Moc wymiennika:</td><td>{model.specs.waterHeaterPower}</td></tr>}
              {model.specs.waterCapacity && <tr><td>Pojemność wymiennika:</td><td>{model.specs.waterCapacity}</td></tr>}
              <tr><td>Szyba:</td><td>{model.specs.glassSize}</td></tr>
              {model.specs.insertSize && <tr><td>Wymiary wkładu:</td><td>{model.specs.insertSize}</td></tr>}
              {model.specs.fireplaceSize && <tr><td>Wymiary paleniska:</td><td>{model.specs.fireplaceSize}</td></tr>}
              {model.specs.maxLogLength && <tr><td>Maks. długość polan:</td><td>{model.specs.maxLogLength}</td></tr>}
              <tr><td>Ciężar:</td><td>{model.specs.weight}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Popup z opisami perków - używa tego samego stylu co inne modale */}
      {showPerkPopup && selectedPerkType && createPortal(
        <div className="variant-popup-overlay" onClick={closePerkPopup}>
          <div className="variant-popup" onClick={(e) => e.stopPropagation()} style={{ minHeight: '300px' }}>
            <div className="variant-popup-header">
              <h3>{getCurrentDescription()?.title || 'Opis perku'}</h3>
              <button className="variant-popup-close" onClick={closePerkPopup}>×</button>
            </div>
            <div className="variant-popup-content" style={{ minHeight: '200px' }}>
              <p>{getCurrentDescription()?.description || 'Brak dostępnego opisu dla tego elementu.'}</p>
              
              {/* Obrazek perka - jeśli istnieje */}
              {(() => {
                const imageFilename = getPerkImageFilename(selectedPerkType, config)
                
                // Pokazuj sekcję tylko jeśli mamy nazwę pliku I nie ma błędu
                if (imageFilename && !perkImageError) {
                  return (
                    <div className="perk-image-container" style={{ marginTop: '15px', textAlign: 'center', position: 'relative', minHeight: '220px' }}>
                      {/* Image container with fixed dimensions */}
                      <div style={{ 
                        width: '100%', 
                        height: '200px', 
                        position: 'relative',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5'
                      }}>
                        {/* Actual image */}
                        <img 
                          src={`images/perks/${imageFilename}`}
                          alt={getCurrentDescription()?.title || 'Perk image'}
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            opacity: perkImageLoaded && !perkImageError ? 1 : 0,
                            transition: 'opacity 0.3s ease-in-out'
                          }}
                          onLoad={() => {
                            setPerkImageLoaded(true)
                            setPerkImageError(false)
                          }}
                          onError={() => {
                            // Przy błędzie ładowania ukryj całą sekcję
                            setPerkImageError(true)
                            setPerkImageLoaded(true) // Ukryj loading placeholder
                          }}
                        />
                        
                        {/* Loading overlay - pokazuje się nad obrazem */}
                        {!perkImageLoaded && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '14px'
                          }}>
                            Ładowanie obrazu...
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }
                // Jeśli nie ma obrazka lub jest błąd, zwróć pusty div o minimalnej wysokości żeby nie skakało
                return <div style={{ minHeight: '20px' }}></div>
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}
    </aside>
  )
}


export default PreviewPanel
